'use client';

import { useState, useEffect } from 'react';

import type { TDynamicData } from '../../types/dynamic-data';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlDescription } from '../ui/html-description';
import { ImageComp } from '../shared/image-comp';
import { fetchDynamicSections } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { useQuery } from '@tanstack/react-query';
import { FileDownload } from './file-download';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const addTargetBlankToLinks = (htmlContent: string): string => {
  if (!htmlContent) return htmlContent;
  return htmlContent.replace(/<a(?![^>]*target=)([^>]*)>/gi, '<a$1 target="_blank">');
};

interface OurActivityTabsProps {
  tabs: TDynamicData[];
  locale: string;
}

export function OurActivityTabs({ tabs, locale }: OurActivityTabsProps) {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.name || '');

  const usefulLinks = locale === 'sk' ? 'Užitočné linky' : 'Useful links';
  const usefulLinksHash = locale === 'sk' ? '#uzitocne-linky' : '#useful-links';

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === usefulLinksHash) {
      setActiveTab(usefulLinks);
    } else {
      setActiveTab(tabs[0]?.name || '');
    }
  }, [tabs]);

  const activeTabData = tabs.find(tab => tab.name === activeTab);

  const { data: documents = [] } = useQuery({
    queryKey: ['TAB_DOCUMENTS', locale, activeTab],
    queryFn: () =>
      fetchDynamicSections<TDynamicData>({
        locale,
        sectionIds: [12],
        query: { valueFilters: [{ fieldId: 27, values: activeTab }] },
      }),
  });

  function convertOembedToIframe(html: string): string {
    return html.replace(/<oembed url="([^"]+)"><\/oembed>/g, (match, url) => {
      let embedUrl = url;

      if (url.includes('youtube.com/watch?v=')) {
        const videoId = url.split('v=')[1];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }

      return `
      <iframe
        width="560"
        height="315"
        src="${embedUrl}"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      ></iframe>
    `;
    });
  }

  const modifiedContent = activeTabData?.fields[0]?.value
    ? addTargetBlankToLinks(convertOembedToIframe(activeTabData.fields[0].value))
    : '';

  return (
    <section className={cn('custom-section', 'mb-10')}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-10 sm:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'flex h-fit w-full items-end justify-start gap-0 overflow-x-scroll rounded-none border-none bg-transparent p-[1px] shadow-none sm:gap-2 sm:overflow-x-hidden',
            'hide-scroll-bar',
          )}
        >
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.name}
              value={tab.name}
              className={cn(
                'ea-about-us__tabs-trigger',
                'h-fit rounded-none border-b-[3px] p-4 text-base font-bold sm:text-lg lg:text-xl',
              )}
            >
              {tab.name}
            </TabsTrigger>
          ))}
          <div className="h-[3px] w-full flex-grow bg-primary/50" />
        </TabsList>
        <TabsContent
          value={activeTab}
          className="flex w-full flex-col-reverse items-start justify-start gap-10 md:flex-row lg:gap-12 xl:gap-20"
        >
          <ImageComp
            alt={activeTab}
            imageUrl={activeTabData?.image?.url ?? ''}
            className={{ container: 'max-w-[357px]' }}
            priority={true}
            loading="eager"
          />
          <div className="flex w-full flex-col items-start justify-start gap-10">
            <h3 className="text-2xl font-bold text-primary sm:text-3xl">
              {activeTabData?.name ?? ''}
            </h3>
            {modifiedContent && (
              <HtmlDescription
                content={modifiedContent}
                className={cn(
                  'w-full md:max-w-[558px] text-justify',
                  'ea-about-us__tabs-description',
                  'ea-our-activity__tabs-description',
                )}
              />
            )}
            {documents.length > 0 && (
              <div className="flex w-full flex-col items-start justify-start gap-4">
                <h4 className="text-lg font-semibold sm:text-xl">{t('text2406')}</h4>
                <div className="flex w-full flex-col items-start justify-start gap-2">
                  {documents.map((doc: TDynamicData) => (
                    <FileDownload item={doc} key={doc.name} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}
